import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AuthService from 'services/authService';
import UserService from 'services/userService';

export default function Navbar({ currentUser: initialUser }) {
  const [showMenu, setShowMenu] = useState(false);
  const [currentUser, setCurrentUser] = useState(initialUser);
  const navigate = useNavigate();
  const location = useLocation();

  // Listen for balance updates
  useEffect(() => {
    const handleBalanceUpdate = () => {
      const updatedUser = UserService.getCurrentUser();
      if (updatedUser) {
        setCurrentUser(updatedUser);
      }
    };

    // Listen for custom balance update event
    window.addEventListener('userBalanceUpdated', handleBalanceUpdate);
    
    return () => {
      window.removeEventListener('userBalanceUpdated', handleBalanceUpdate);
    };
  }, []);

  // Update currentUser when initialUser prop changes
  useEffect(() => {
    setCurrentUser(initialUser);
  }, [initialUser]);

  function handleLogout() {
    AuthService.logout();
    navigate('/login', { replace: true });
  }

  function toggleMenu() {
    setShowMenu(!showMenu);
  }

  return (
    <div className="fixed w-full z-10">
      <div
        className="w-full
        flex justify-between items-center 
        px-6 py-4
        bg-[#0A0A0A] select-none
      "
      >
        <div
          className="
          flex items-center justify-center gap-2
          cursor-pointer text-white"
          onClick={() => {
            navigate('/', { replace: true });
          }}
        >
          <img src="/logo.png" alt="logo" className="w-10 h-10" />
          <h3 className="text-2xl font-mono font-semibold"> Musoe</h3>
        </div>

        <div className="flex text-white items-center justify-center gap-3">
          {!location.pathname.startsWith('/admin') && (
            <div className="flex items-center justify-between rounded-xl bg-[#272727]">
              <div className="flex items-center justify-center w-12 h-12">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3"
                  />
                </svg>
              </div>
              <div className="pr-2 font-semibold">Balance: {currentUser.balance} 🪙</div>
            </div>
          )}

          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#272727] bg-ble cursor-pointer" onClick={toggleMenu}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-user-round"
            >
              <circle cx="12" cy="8" r="5" />
              <path d="M20 21a8 8 0 0 0-16 0" />
            </svg>
          </div>
        </div>

        {showMenu && (
          <div
            className="
                absolute right-4 top-20 z-10
                w-48 duration-100
                bg-[#272727] text-white
                rounded-2xl shadow-lg
                cursor-pointer
                "
          >
            <ul className="p-2 font-bold">
              {currentUser.role === 'admin' && (
                <li className="p-4 flex items-center gap-4 hover:bg-[#363636] rounded-xl" onClick={() => navigate('/admin')}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-app-window-mac"
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="M6 8h.01" />
                    <path d="M10 8h.01" />
                    <path d="M14 8h.01" />
                  </svg>
                  Admin Panel
                </li>
              )}
              <li className="p-4 flex items-center gap-4 hover:bg-[#363636] rounded-xl" onClick={() => navigate('/profile')}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-user-round-cog"
                >
                  <path d="M2 21a8 8 0 0 1 10.434-7.62" />
                  <circle cx="10" cy="8" r="5" />
                  <circle cx="18" cy="18" r="3" />
                  <path d="m19.5 14.3-.4.9" />
                  <path d="m16.9 20.8-.4.9" />
                  <path d="m21.7 19.5-.9-.4" />
                  <path d="m15.2 16.9-.9-.4" />
                  <path d="m21.7 16.5-.9.4" />
                  <path d="m15.2 19.1-.9.4" />
                  <path d="m19.5 21.7-.4-.9" />
                  <path d="m16.9 15.2-.4-.9" />
                </svg>
                Profile
              </li>
              <li className="p-4 flex items-center gap-4 hover:bg-[#363636] rounded-xl" onClick={() => navigate('/orders')}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-clipboard-list"
                >
                  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <path d="M12 11h4" />
                  <path d="M12 16h4" />
                  <path d="M8 11h.01" />
                  <path d="M8 16h.01" />
                </svg>
                Orders
              </li>
              <div className="border-t border-[#363636] my-2" />
              <li className="p-4 flex items-center gap-4 hover:bg-[#363636] rounded-xl" onClick={handleLogout}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-log-out"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" x2="9" y1="12" y2="12" />
                </svg>
                Logout
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
